import React from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import "@/index.css";
import { AuthProvider } from "@/lib/auth";
import { COMPANY } from "@/lib/constants";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Uslugi from "@/pages/Uslugi";
import Realizacje from "@/pages/Realizacje";
import ONas from "@/pages/ONas";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Kontakt from "@/pages/Kontakt";
import Admin from "@/pages/Admin";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const SmoothScroll = () => {
  React.useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-ink text-pearl flex items-center justify-center p-8">
          <p className="text-stonemute">Coś poszło nie tak. Odśwież stronę lub zadzwoń: 535 616 324</p>
        </div>
      );
    }
    return this.props.children;
  }
}

const StickyCall = () => (
  <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-2" data-testid="sticky-contact-pills">
    <a
      href={COMPANY.whatsapp}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="sticky-whatsapp"
      className="flex items-center gap-2 border border-white/10 bg-graph2 px-5 py-3 font-mono text-xs uppercase tracking-widest text-pearl shadow-[0_10px_40px_rgba(0,0,0,0.45)] transition-colors duration-300 hover:border-[#25D366]"
    >
      <WhatsAppIcon className="text-[#25D366]" />
      WhatsApp
    </a>
    <a
      href="tel:+48535616324"
      data-testid="sticky-call-pill"
      className="btn-amber flex items-center gap-2 px-5 py-3 font-mono text-xs font-medium uppercase tracking-widest shadow-[0_10px_40px_rgba(255,184,0,0.25)]"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
      535 616 324
    </a>
  </div>
);

const Layout = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
    <StickyCall />
  </>
);

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <SmoothScroll />
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Layout><Home /></Layout>} />
              <Route path="/uslugi" element={<Layout><Uslugi /></Layout>} />
              <Route path="/realizacje" element={<Layout><Realizacje /></Layout>} />
              <Route path="/o-nas" element={<Layout><ONas /></Layout>} />
              <Route path="/blog" element={<Layout><Blog /></Layout>} />
              <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
              <Route path="/kontakt" element={<Layout><Kontakt /></Layout>} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<Layout><Home /></Layout>} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
